import type { ReactElement } from 'react';

import { defaultFormat, describeRange, describeSeries, tableRows, type ValueFormatter } from './alternative';
import { Axes, pickXLabels, plotFrame } from './Axes';
import { ChartFrame } from './ChartFrame';
import { computeDomain, isValue, linearScale } from './geometry';
import { Legend } from './Legend';
import { seriesTokenAt, useTokens } from './seriesPalette';
import type { LineSeries } from './LineChart';
import { useMeasuredWidth } from './useMeasuredWidth';

/**
 * Barres empilees (maquette « Saisies par jour », une serie par nature).
 *
 * Une colonne par categorie, un segment par serie, **empiles dans l'ordre de
 * la legende** (la premiere serie en bas). Un segment est separe du suivant
 * par un filet de la couleur de la carte, pour que deux aplats voisins se lisent
 * meme quand leurs teintes se ressemblent.
 *
 * - une valeur `null` ne dessine **aucun segment** : une colonne dont toutes les
 *   valeurs manquent est vide, jamais une colonne basse ;
 * - un zero reel ne dessine rien non plus, mais se lit `0` dans la table ;
 * - une valeur strictement positive occupe au moins 1 px, pour rester visible.
 *
 * - **valeurs negatives non supportees** : les donnees servies sont des comptes. Un
 *   negatif est traite comme absent pour le dessin et l'axe (il ne coupe ni ne
 *   fausse aucune barre) ; la table, elle, ecrit la valeur fournie.
 *
 * Les totaux ne sont ni affiches ni ecrits : le graphique empile ce qu'on lui
 * passe, il n'en tire aucun chiffre.
 */
export interface StackedBarChartProps {
  readonly label: string;
  readonly categories: readonly string[];
  /** Une serie par segment, dans l'ordre de l'empilement (bas vers haut). */
  readonly series: readonly Pick<LineSeries, 'label' | 'values' | 'token'>[];
  readonly categoryHeader?: string;
  readonly formatValue?: ValueFormatter;
  readonly max?: number;
  readonly height?: number;
  readonly summary?: string;
  readonly emptyMessage?: string;
  /** Cellule de la table pour une valeur absente, quand « — » en dirait moins (« < 10 »). */
  readonly missingLabel?: string;
}

const FALLBACK_WIDTH = 600;
/** Part de la largeur d'une colonne occupee par la barre (maquette : 0,55 a 0,62). */
const BAR_RATIO = 0.62;

export const StackedBarChart = ({
  label,
  categories,
  series,
  categoryHeader = 'Date',
  formatValue = defaultFormat,
  max,
  height = 220,
  summary,
  emptyMessage = 'Aucune donnée sur la période.',
  missingLabel,
}: StackedBarChartProps): ReactElement => {
  const tokens = useTokens();
  const { ref, width } = useMeasuredWidth(FALLBACK_WIDTH);

  const columns = categories.map((_, index) =>
    series.map((one) => {
      const value = one.values[index];

      // Des comptes : un negatif n'est ni empile ni compte dans l'axe (voir l'en-tete).
      return isValue(value) && value >= 0 ? value : null;
    }),
  );
  const totals = columns.map((column) => column.reduce<number>((sum, value) => sum + (value ?? 0), 0));
  const hasData = columns.some((column) => column.some((value) => value !== null));

  const domain = computeDomain(0, hasData ? Math.max(...totals) : 0, {
    integers: series.every((one) => one.values.every((value) => !isValue(value) || Number.isInteger(value))),
    ...(max === undefined ? {} : { max }),
  });
  const frame = plotFrame(width, height, domain, formatValue);
  const slot = frame.plotWidth / Math.max(categories.length, 1);
  const barWidth = Math.max(2, slot * BAR_RATIO);
  const xCenter = (index: number): number => frame.left + slot * index + slot / 2;
  const baseline = frame.top + frame.plotHeight;
  const yAt = linearScale(domain.min, domain.max, baseline, frame.top);

  const resolved = series.map((one, rank) => ({ ...one, token: one.token ?? seriesTokenAt(rank) }));

  return (
    <ChartFrame
      ref={ref}
      label={label}
      summary={
        summary ??
        [describeRange(categories), ...series.map((one) => describeSeries(one.label, categories, one.values, formatValue))].join(' ')
      }
      table={{ categoryHeader, columns: series.map((one) => one.label), rows: tableRows(categories, series, formatValue, missingLabel) }}
      {...(hasData ? {} : { empty: emptyMessage })}
    >
      <svg width={width} height={height} focusable="false" style={{ display: 'block', maxWidth: '100%' }}>
        <Axes frame={frame} domain={domain} yAt={yAt} format={formatValue} xLabels={pickXLabels(frame, categories, xCenter)} />
        {columns.map((column, index) => {
          let top = baseline;

          return (
            // L'abscisse peut se repeter : le rang est l'identite de la colonne.
            <g key={index}>
              {column.map((value, rank) => {
                if (value === null || value <= 0) {
                  return null;
                }

                const segment = Math.max(1, baseline - yAt(value));
                top -= segment;

                return (
                  <rect
                    key={rank}
                    x={xCenter(index) - barWidth / 2}
                    y={top}
                    width={barWidth}
                    height={segment}
                    fill={tokens[resolved[rank]!.token]}
                    stroke={tokens.surface}
                    strokeWidth={1}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
      <Legend items={resolved.map((one) => ({ label: one.label, token: one.token, shape: 'square' }))} />
    </ChartFrame>
  );
};
